import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const slides = [
  { type: "image", src: "/images/slide1.png", alt: "Campus Buildings" },
  { type: "image", src: "/images/slides.jpg", alt: "Campus Buildings" },
  { type: "video", src: "/images/bgvd.mp4", alt: "Campus Life Video" },
  { type: "image", src: "/images/slide2.png", alt: "Campus Buildings" },
  { type: "image", src: "/images/bgim2.jpg", alt: "Library Interior" },
  { type: "video", src: "/images/slidev.mp4", alt: "Sports Event Video" },
  { type: "image", src: "/images/ibb.jpg", alt: "Campus Front View" },
  { type: "image", src: "/images/acp2.jpg", alt: "Students Group" },
  { type: "image", src: "/images/bgim3.jpg", alt: "Campus Buildings" },
];

const HeroSlider = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  const currentSlide = slides[current];

  return (
    <section className="relative w-full h-screen overflow-hidden">
      {/* Background Slide - Only render current slide */}
      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          className="absolute inset-0 w-full h-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
        >
          {currentSlide.type === "image" ? (
            <img
              src={currentSlide.src}
              alt={currentSlide.alt}
              className="w-full h-full object-cover"
              loading={current === 0 ? "eager" : "lazy"}
            />
          ) : (
            <video
              src={currentSlide.src}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
              preload="metadata"
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Gradient Overlay - Enhanced contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/05 via-black/0 to-black/05 pointer-events-none" />

      {/* Content Container - Centered with constraints */}
      <div className="absolute inset-0 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="max-w-4xl w-full text-center"
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white drop-shadow-2xl mb-4 leading-tight px-4">
            Welcome to Our Campus
          </h1>
          <p className="mt-4 text-base sm:text-lg md:text-xl text-blue-1000 drop-shadow-lg mb-8 px-4">
            Inspiring Excellence, Innovation, and Growth
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4">
            <Link to="/admissions">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                animate={{
                  y: [0, -4, 0],
                  boxShadow: [
                    "0 4px 20px rgba(59,130,246,0.4)",
                    "0 8px 30px rgba(59,130,246,0.6)",
                    "0 4px 20px rgba(59,130,246,0.4)",
                  ],
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-3.5 rounded-full font-semibold shadow-xl transition-all duration-300 min-w-[180px]"
              >
                Explore Programs
              </motion.button>
            </Link>

            <Link to="/apply">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                animate={{
                  y: [0, -4, 0],
                  boxShadow: [
                    "0 4px 20px rgba(255,255,255,0.3)",
                    "0 8px 30px rgba(255,255,255,0.5)",
                    "0 4px 20px rgba(255,255,255,0.3)",
                  ],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="w-full sm:w-auto bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-8 py-3.5 rounded-full font-semibold shadow-xl transition-all duration-300 border border-white/30 min-w-[180px]"
              >
                Apply Now
              </motion.button>
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Navigation Dots - Properly contained */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center items-center gap-3 px-4">
        {slides.map((slide, index) => {
          const isActive = index === current;
          const isVideo = slide.type === "video";

          return (
            <motion.button
              key={index}
              onClick={() => setCurrent(index)}
              aria-label={`Go to slide ${index + 1}`}
              className={`rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent ${
                isActive ? "w-10 h-3 bg-white" : "w-3 h-3 bg-white/50 hover:bg-white/70"
              }`}
              animate={
                isActive
                  ? {
                      boxShadow: isVideo
                        ? [
                            "0 0 0px rgba(6,182,212,0.5)",
                            "0 0 16px rgba(6,182,212,0.9)",
                            "0 0 0px rgba(6,182,212,0.5)",
                          ]
                        : [
                            "0 0 0px rgba(255,255,255,0.4)",
                            "0 0 16px rgba(255,255,255,0.9)",
                            "0 0 0px rgba(255,255,255,0.4)",
                          ],
                    }
                  : {}
              }
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            />
          );
        })}
      </div>

      {/* Slide Counter - Accessibility */}
      <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium">
        {current + 1} / {slides.length}
      </div>
    </section>
  );
};

export default HeroSlider;